import * as _ from 'underscore';
import * as $ from 'jquery';

import { config } from '../../../pdc-platform/src/ts/config';
import { IValuesCollection } from './appTheme';
import { themeVars } from '../../../pdc-platform/src/ts/themes/themeConstants';

const Globalize = require('pdc-platform-Globalize');

export const DEFAULT_FISCAL_FIRST_MONTH = 1;
export const DEFAULT_FIRST_WEEK_DAY_NUMBER = 7;

export interface ICultureValues {
    culture: string;
    firstWeekDay: string;
    firstWeekDayNumber: number;
    fiscalFirstMonth: number;
}

export function getCultureCodes() {
    const locales = config.SUPPORTED_LOCALES.split(',').map((locale) => {
        return locale.trim();
    });

    return _.chain(cultureCodes)
        .filter((entry) => {
            let cult_lang = entry.code.split('-');

            return _.contains(locales, cult_lang[0]);
        })
        .map((entry) => {
            return entry.code;
        })
        .value();
}

export function getCultureCodesWithDefault() {
    return ['default'].concat(getCultureCodes());
}

export function getCultureNames() {
    const locales = _.map(config.SUPPORTED_LOCALES.split(','), (l) => {
        return l.trim();
    });

    return _.chain(cultureCodes)
        .filter((entry) => {
            let cult_lang = entry.code.split('-');
            return _.contains(locales, cult_lang[0]);
        })
        .map((entry) => {
            return entry.name + ' (' + entry.code + ')';
        })
        .value();
}

export function getCultureNamesWithDefault() {
    return ['<default> - Use Browser Default Culture'].concat(getCultureNames());
}

export function checkCurrency(valCur) {
    //VR: for checking the currency abbreviation entered by User in Theme.Culture
    try {
        Globalize.formatCurrency(9.99, valCur);
    } catch (e) {
        return ['Such abbreviation is not allowed.'];
    }
    return;
}

export function getAppliedLocale() {
    const result = Globalize.locale();

    return result != null ? result.locale : null;
}

export function getDefaultCulture(): string {
    const lang = (<any>navigator).languages
        ? (<any>navigator).languages[0]
        : (<any>navigator).language || (<any>navigator).userLanguage;

    return lang;
}

export function getActualCulture(culture?: string): string {
    return culture == null || culture.toLowerCase() === 'default' ? getDefaultCulture() : culture;
}

export function applyCulture(culture: string, callback?: (culture: string) => void) {
    if (culture == null) {
        culture = 'default';
    }

    const actualCulture = getActualCulture(culture);

    //console.log('Apply Culture: ' + actualCulture);
    cultureLoader(actualCulture, callback);
}

export function getCultureValues(theme: IValuesCollection, culture?: string): ICultureValues {
    let fiscalFirstMonth: number;

    if (theme != null) {
        if (culture == null) {
            culture = getWidgetCulture(theme);
        }

        fiscalFirstMonth = theme[themeVars.fiscalFirstMonth]; //LL: may be string ~~"4"  for April

        if (fiscalFirstMonth != null) {
            if (typeof fiscalFirstMonth === 'string') {
                fiscalFirstMonth = parseInt(fiscalFirstMonth, 10);

                if (isNaN(fiscalFirstMonth)) {
                    fiscalFirstMonth = DEFAULT_FISCAL_FIRST_MONTH;
                }
            }
        }
    }

    if (fiscalFirstMonth == null) {
        fiscalFirstMonth = DEFAULT_FISCAL_FIRST_MONTH;
    }

    const actualCulture = getActualCulture(culture);
    const firstWeekDay = getFirstWeekDay(theme);

    return {
        culture: actualCulture,
        fiscalFirstMonth: fiscalFirstMonth,
        firstWeekDay: firstWeekDay,
        firstWeekDayNumber: weekDayNumber_iso(firstWeekDay)
    };
}

function getSelectedCulture(theme: IValuesCollection) {
    if (theme != null) {
        return theme['widget-culture-selector'];
    }

    return undefined;
}

export function getWidgetCulture(theme: IValuesCollection) {
    return getSelectedCulture(theme) ?? 'default';
}

export function applyWidgetCulture(theme: IValuesCollection, callback?: (culture: string) => void) {
    const widgetCulture = getWidgetCulture(theme);

    applyCulture(widgetCulture, callback);
}

export function applyWidgetCultureWhenNeeded(theme: IValuesCollection) {
    const widgetCulture = getWidgetCulture(theme);
    const actualCulture = getActualCulture(widgetCulture);
    const locale = getLocale(actualCulture);
    const appliedLocale = getAppliedLocale();

    if (locale !== appliedLocale) {
        cultureLoader(actualCulture);
    }
}

export function getWidgetLocale(theme: IValuesCollection) {
    const culture = getSelectedCulture(theme);

    if (culture != null) {
        return getLocale(culture);
    }

    return undefined;
}

export const loadedCultures: { [name: string]: boolean } = {}; //VR: let's mark it as PUBLIC for JEST testing.

export function getLocale(culture: string) {
    const cultureLang = culture.split('-');

    return cultureLang[0];
}

export function cultureLoader(culture: string, callback?: (culture: string) => void) {
    const locale = getLocale(culture);

    const callbackInternal = (culture: string) => {
        Globalize.locale(culture);
        //LL: 05.08.21: for WI 117767
        // (Application should do fallback to EN-US locale when the default browser's locale is not included into the distribution)
        //   see https://github.com/globalizejs/globalize/issues/302
        //   https://stackoverflow.com/questions/53092609/globalize-js-failing-with-e-missing-bundle-even-though-cldr-is-loaded
        //LL: just try, Chart not shown, error in console: e.message = E_MISSING_BUNDLE: {"locale":"ar"}
        //LL: but error is not here, try-catch does not work, locale is just not set !
        //Globalize.locale('ar'); //LL: try missing culture
        const locale = Globalize.locale();

        let localeLoaded = true;

        if (locale == null || locale.attributes == null || locale.attributes.bundle == null) {
            console.log('Globalization.cultureLoader: locale not set: culture = ' + culture);
            localeLoaded = false;
            Globalize.locale('en');
        }

        loadedCultures[culture] = localeLoaded;

        if (callback != null) {
            callback(culture);
        }
    };

    const cultureIsLoaded = loadedCultures[locale];

    if (cultureIsLoaded == null) {
        // Feed Globalize with CLDR data
        $.when(
            $.getJSON('_dist/cldr-data/main/' + locale + '/ca-gregorian.json'),
            $.getJSON('_dist/cldr-data/main/' + locale + '/numbers.json'),
            $.getJSON('_dist/cldr-data/main/' + locale + '/currencies.json'),
            $.getJSON('_dist/cldr-data/main/' + locale + '/timeZoneNames.json'),
            $.getJSON('_dist/cldr-data/supplemental/likelySubtags.json'),
            $.getJSON('_dist/cldr-data/supplemental/timeData.json'),
            $.getJSON('_dist/cldr-data/supplemental/weekData.json'),
            $.getJSON('_dist/cldr-data/supplemental/currencyData.json'),
            $.getJSON('_dist/cldr-data/supplemental/numberingSystems.json'),
            $.getJSON('_dist/cldr-data/supplemental/plurals.json')
        )
            .then(function () {
                //converts the got results into an array
                return [].slice.apply(arguments, [0]).map(function (result) {
                    return result[0];
                });
            })
            .then(
                Globalize.load //loads data held in each array item to Globalize
            )
            .then(() => {
                callbackInternal(locale);
            })
            .fail(() => {
                if (locale.toUpperCase() !== 'EN') {
                    // VR: exclude this annoying warning if EN !!!
                    console.warn(
                        'Localization Data for locale "' +
                            locale +
                            '" is not included into assembly. Falling back to EN'
                    );
                }

                loadedCultures[locale] = false;
                callbackInternal('en');
            });
    } else {
        if (cultureIsLoaded) {
            callbackInternal(locale);
        } else {
            callbackInternal('en');
        }
    }
}

export function isDate(x) {
    return null != x && !isNaN(x) && 'undefined' !== typeof x.getDate;
}

export function parseDate(value, options: { raw: string }): Date {
    return Globalize.parseDate(value, options);
}

export function formatDate(value: any, options?) {
    if (!isDate(value)) {
        value = new Date(value);
    }

    return Globalize.formatDate(value, options);
}

export function formatNumber(value: any, options?) {
    return Globalize.formatNumber(value, options);
}

//'currencyCode'- 3-letter currency code as defined by ISO 4217, eg. "USD".
export function formatCurrency(value: any, currencyCode: string, options?) {
    return Globalize.formatCurrency(value, currencyCode, options);
}

export function getCLDRValue(path: string) {
    return Globalize.cldr.main(path);
}

export function getPrefferedTimeFormat(culture: string) {
    const actualCulture = getActualCulture(culture);
    const globalize = Globalize(actualCulture);
    const preffered = globalize.cldr.supplemental.timeData.preferred();

    return preffered;
}

export function getMonthName_Wide(monthNumber: number) {
    return Globalize.cldr.main('dates/calendars/gregorian/months/stand-alone/wide/' + monthNumber);
}

export function getMonthName_Abbreviated(monthNumber: number): string {
    return Globalize.cldr.main('dates/calendars/gregorian/months/stand-alone/abbreviated/' + monthNumber);
}

export function getWeekDayName_Wide(dayNumber: number): string {
    return getWeekDayNameByAbbreviation_Wide(weekDayAbbreviated(dayNumber));
}

export function getWeekDayNameByAbbreviation_Wide(abbreviatedDay: string): string {
    return Globalize.cldr.main('dates/calendars/gregorian/days/stand-alone/wide/' + abbreviatedDay);
}

export function getWeekDayName_Abbreviated(dayNumber: number): string {
    return Globalize.cldr.main(
        'dates/calendars/gregorian/days/stand-alone/abbreviated/' + weekDayAbbreviated(dayNumber)
    );
}

export function getWeekDayName_Narrow(dayNumber: number): string {
    return Globalize.cldr.main('dates/calendars/gregorian/days/stand-alone/narrow/' + weekDayAbbreviated(dayNumber));
}

export const WEEK_DAY_ABBREVIATIONS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
export const WEEK_DAY_ABBREVIATIONS_ISO = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export function weekDayAbbreviated(dayNumber: number) {
    const firstWeekDay = getFirstDay();
    const firstWeekDayIndex = WEEK_DAY_ABBREVIATIONS.indexOf(firstWeekDay);

    let idx = dayNumber + firstWeekDayIndex;

    if (idx > 6) {
        idx = idx - 7;
    }

    return WEEK_DAY_ABBREVIATIONS[idx];
}

export function getFirstWeekDay(theme: IValuesCollection): string {
    let result: string;

    if (theme != null) {
        result = theme['widget-culture-first-week-day'];

        if (result == null) {
            result = getFirstDay(getWidgetCulture(theme));
        }
    }

    if (result == null) {
        result = getFirstDay();
    }

    return result;
}

export function getFirstDay(culture?: string): string {
    if (culture != null) {
        const globalize = Globalize(getActualCulture(culture));

        return globalize.cldr.supplemental.weekData.firstDay();
    } else {
        return Globalize.cldr.supplemental.weekData.firstDay();
    }
}

const WEEK_DAY_NUMBERS = {
    sun: 0,
    mon: 1,
    tue: 2,
    wed: 3,
    thu: 4,
    fri: 5,
    sat: 6
};

export function weekDayNumber(w: string): number {
    return WEEK_DAY_NUMBERS[w];
}

const WEEK_DAY_NUMBERS_ISO = {
    sun: 7,
    mon: 1,
    tue: 2,
    wed: 3,
    thu: 4,
    fri: 5,
    sat: 6
};

export function weekDayNumber_iso(weekDay: string): number {
    return WEEK_DAY_NUMBERS_ISO[weekDay];
}

export function getFirstWeekDayNumber(culture?: string): number {
    const firstDay = getFirstDay(culture);

    return weekDayNumber(firstDay);
}

export function getFirstWeekDayNumber_ISO(culture?: string): number {
    const firstDay = getFirstDay(culture);

    return weekDayNumber_iso(firstDay);
}

export function getWeekDayNumbers() {
    return [0, 1, 2, 3, 4, 5, 6];
}

export function getWeekDaynames(culture: string) {
    const globalize = Globalize(culture);

    return getWeekDayNumbers().map((w) => {
        return globalize.cldr.main('dates/calendars/gregorian/days/stand-alone/wide/' + weekDayAbbreviated(w));
    });
}

export function getMonths() {
    const result: {
        type: string;
        name: string;
    }[] = [];

    for (let i = 1; i <= 12; i++) {
        result.push({
            type: i.toString(),
            name: getMonthName_Wide(i)
        });
    }

    return result;
}

// Extracxt from globalize
export function formatMessage(message, data) {
    // Replace {attribute}'s
    message = message.replace(/{[0-9a-zA-Z-_. ]+}/g, function (name) {
        name = name.replace(/^{([^}]*)}$/, '$1');
        return data[name];
    });

    return message;
}

export function apply24hours(pattern: string, use24hours?: boolean) {
    if (use24hours === true) {
        pattern = pattern.replace('h:', 'HH:');
        pattern = pattern.replace('h.', 'HH.');
        pattern = pattern.replace(/a$/, '');
    }

    if (use24hours === false) {
        pattern = pattern.replace('HH:', 'h:');
        pattern = pattern.replace('HH.', 'h.');

        if (!pattern.match(/a$/)) {
            pattern = pattern + ' a';
        }
    }

    return pattern;
}

// Optionally converts the DevExtreme format name into CLDR format specifier
export function getFormatSpecifier(format: string, use24hours?: boolean): { raw: string } | string {
    let pattern: string;

    switch (format) {
        case 'shortDate':
            pattern = getCLDRValue('dates/calendars/gregorian/dateFormats/short');
            return { raw: apply24hours(pattern, use24hours) };

        case 'longDate':
            pattern = getCLDRValue('dates/calendars/gregorian/dateFormats/long');
            return { raw: apply24hours(pattern, use24hours) };

        case 'shortDateShortTime':
        case 'dateShortTime':
            pattern = formatMessage(getCLDRValue('dates/calendars/gregorian/dateTimeFormats/short'), [
                getCLDRValue('dates/calendars/gregorian/timeFormats/short'),
                getCLDRValue('dates/calendars/gregorian/dateFormats/short')
            ]);
            return { raw: apply24hours(pattern, use24hours) };

        case 'dateLongTime':
            pattern = formatMessage(getCLDRValue('dates/calendars/gregorian/dateTimeFormats/medium'), [
                getCLDRValue('dates/calendars/gregorian/timeFormats/medium'),
                getCLDRValue('dates/calendars/gregorian/dateFormats/long')
            ]);
            return { raw: apply24hours(pattern, use24hours) };

        case 'longDateLongTime':
            pattern = formatMessage(getCLDRValue('dates/calendars/gregorian/dateTimeFormats/medium'), [
                getCLDRValue('dates/calendars/gregorian/timeFormats/medium'),
                getCLDRValue('dates/calendars/gregorian/dateFormats/full')
            ]);
            return { raw: apply24hours(pattern, use24hours) };

        case 'longTime':
            pattern = getCLDRValue('dates/calendars/gregorian/timeFormats/medium');
            return { raw: apply24hours(pattern, use24hours) };

        case 'shortTime':
            pattern = getCLDRValue('dates/calendars/gregorian/timeFormats/short');
            return { raw: apply24hours(pattern, use24hours) };

        default:
            return format;
    }
}

const cultureCodes = [
    {
        code: 'af-ZA',
        name: 'Afrikaans - South Africa'
    },
    {
        code: 'sq-AL',
        name: 'Albanian - Albania'
    },
    {
        code: 'ar-DZ',
        name: 'Arabic - Algeria'
    },
    {
        code: 'ar-BH',
        name: 'Arabic - Bahrain'
    },
    {
        code: 'ar-EG',
        name: 'Arabic - Egypt'
    },
    {
        code: 'ar-IQ',
        name: 'Arabic - Iraq'
    },
    {
        code: 'ar-JO',
        name: 'Arabic - Jordan'
    },
    {
        code: 'ar-KW',
        name: 'Arabic - Kuwait'
    },
    {
        code: 'ar-LB',
        name: 'Arabic - Lebanon'
    },
    {
        code: 'ar-LY',
        name: 'Arabic - Libya'
    },
    {
        code: 'ar-MA',
        name: 'Arabic - Morocco'
    },
    {
        code: 'ar-OM',
        name: 'Arabic - Oman'
    },
    {
        code: 'ar-QA',
        name: 'Arabic - Qatar'
    },
    {
        code: 'ar-SA',
        name: 'Arabic - Saudi Arabia'
    },
    {
        code: 'ar-SY',
        name: 'Arabic - Syria'
    },
    {
        code: 'ar-TN',
        name: 'Arabic - Tunisia'
    },
    {
        code: 'ar-AE',
        name: 'Arabic - United Arab Emirates'
    },
    {
        code: 'ar-YE',
        name: 'Arabic - Yemen'
    },
    {
        code: 'hy-AM',
        name: 'Armenian - Armenia'
    },
    {
        code: 'Cy-az-AZ',
        name: 'Azeri (Cyrillic) - Azerbaijan'
    },
    {
        code: 'Lt-az-AZ',
        name: 'Azeri (Latin) - Azerbaijan'
    },
    {
        code: 'eu-ES',
        name: 'Basque - Basque'
    },
    {
        code: 'be-BY',
        name: 'Belarusian - Belarus'
    },
    {
        code: 'bg-BG',
        name: 'Bulgarian - Bulgaria'
    },
    {
        code: 'ca-ES',
        name: 'Catalan - Catalan'
    },
    {
        code: 'zh-CN',
        name: 'Chinese - China'
    },
    {
        code: 'zh-HK',
        name: 'Chinese - Hong Kong SAR'
    },
    {
        code: 'zh-MO',
        name: 'Chinese - Macau SAR'
    },
    {
        code: 'zh-SG',
        name: 'Chinese - Singapore'
    },
    {
        code: 'zh-TW',
        name: 'Chinese - Taiwan'
    },
    {
        code: 'zh-CHS',
        name: 'Chinese (Simplified)'
    },
    {
        code: 'zh-CHT',
        name: 'Chinese (Traditional)'
    },
    {
        code: 'hr-HR',
        name: 'Croatian - Croatia'
    },
    {
        code: 'cs-CZ',
        name: 'Czech - Czech Republic'
    },
    {
        code: 'da-DK',
        name: 'Danish - Denmark'
    },
    {
        code: 'div-MV',
        name: 'Dhivehi - Maldives'
    },
    {
        code: 'nl-BE',
        name: 'Dutch - Belgium'
    },
    {
        code: 'nl-NL',
        name: 'Dutch - The Netherlands'
    },
    {
        code: 'en-AU',
        name: 'English - Australia'
    },
    {
        code: 'en-BE',
        name: 'English - Belgium'
    },
    {
        code: 'en-BZ',
        name: 'English - Belize'
    },
    {
        code: 'en-CA',
        name: 'English - Canada'
    },
    {
        code: 'en-CB',
        name: 'English - Caribbean'
    },
    {
        code: 'en-IE',
        name: 'English - Ireland'
    },
    {
        code: 'en-JM',
        name: 'English - Jamaica'
    },
    {
        code: 'en-NZ',
        name: 'English - New Zealand'
    },
    {
        code: 'en-PH',
        name: 'English - Philippines'
    },
    {
        code: 'en-ZA',
        name: 'English - South Africa'
    },
    {
        code: 'en-TT',
        name: 'English - Trinidad and Tobago'
    },
    {
        code: 'en-GB',
        name: 'English - United Kingdom'
    },
    {
        code: 'en-US',
        name: 'English - United States'
    },
    {
        code: 'en-ZW',
        name: 'English - Zimbabwe'
    },
    {
        code: 'et-EE',
        name: 'Estonian - Estonia'
    },
    {
        code: 'fo-FO',
        name: 'Faroese - Faroe Islands'
    },
    {
        code: 'fa-IR',
        name: 'Farsi - Iran'
    },
    {
        code: 'fi-FI',
        name: 'Finnish - Finland'
    },
    {
        code: 'fr-BE',
        name: 'French - Belgium'
    },
    {
        code: 'fr-CA',
        name: 'French - Canada'
    },
    {
        code: 'fr-FR',
        name: 'French - France'
    },
    {
        code: 'fr-LU',
        name: 'French - Luxembourg'
    },
    {
        code: 'fr-MC',
        name: 'French - Monaco'
    },
    {
        code: 'fr-CH',
        name: 'French - Switzerland'
    },
    {
        code: 'gl-ES',
        name: 'Galician - Galician'
    },
    {
        code: 'ka-GE',
        name: 'Georgian - Georgia'
    },
    {
        code: 'de-AT',
        name: 'German - Austria'
    },
    {
        code: 'de-DE',
        name: 'German - Germany'
    },
    {
        code: 'de-LI',
        name: 'German - Liechtenstein'
    },
    {
        code: 'de-LU',
        name: 'German - Luxembourg'
    },
    {
        code: 'de-CH',
        name: 'German - Switzerland'
    },
    {
        code: 'el-GR',
        name: 'Greek - Greece'
    },
    {
        code: 'gu-IN',
        name: 'Gujarati - India'
    },
    {
        code: 'he-IL',
        name: 'Hebrew - Israel'
    },
    {
        code: 'hi-IN',
        name: 'Hindi - India'
    },
    {
        code: 'hu-HU',
        name: 'Hungarian - Hungary'
    },
    {
        code: 'is-IS',
        name: 'Icelandic - Iceland'
    },
    {
        code: 'id-ID',
        name: 'Indonesian - Indonesia'
    },
    {
        code: 'it-IT',
        name: 'Italian - Italy'
    },
    {
        code: 'it-CH',
        name: 'Italian - Switzerland'
    },
    {
        code: 'ja-JP',
        name: 'Japanese - Japan'
    },
    {
        code: 'kn-IN',
        name: 'Kannada - India'
    },
    {
        code: 'kk-KZ',
        name: 'Kazakh - Kazakhstan'
    },
    {
        code: 'kok-IN',
        name: 'Konkani - India'
    },
    {
        code: 'ko-KR',
        name: 'Korean - Korea'
    },
    {
        code: 'ky-KZ',
        name: 'Kyrgyz - Kazakhstan'
    },
    {
        code: 'lv-LV',
        name: 'Latvian - Latvia'
    },
    {
        code: 'lt-LT',
        name: 'Lithuanian - Lithuania'
    },
    {
        code: 'mk-MK',
        name: 'Macedonian (FYROM)'
    },
    {
        code: 'ms-BN',
        name: 'Malay - Brunei'
    },
    {
        code: 'ms-MY',
        name: 'Malay - Malaysia'
    },
    {
        code: 'mr-IN',
        name: 'Marathi - India'
    },
    {
        code: 'mn-MN',
        name: 'Mongolian - Mongolia'
    },
    {
        code: 'nb-NO',
        name: 'Norwegian (Bokmål) - Norway'
    },
    {
        code: 'nn-NO',
        name: 'Norwegian (Nynorsk) - Norway'
    },
    {
        code: 'pl-PL',
        name: 'Polish - Poland'
    },
    {
        code: 'pt-BR',
        name: 'Portuguese - Brazil'
    },
    {
        code: 'pt-PT',
        name: 'Portuguese - Portugal'
    },
    {
        code: 'pa-IN',
        name: 'Punjabi - India'
    },
    {
        code: 'ro-RO',
        name: 'Romanian - Romania'
    },
    {
        code: 'ru-RU',
        name: 'Russian - Russia'
    },
    {
        code: 'sa-IN',
        name: 'Sanskrit - India'
    },
    {
        code: 'Cy-sr-SP',
        name: 'Serbian (Cyrillic) - Serbia'
    },
    {
        code: 'Lt-sr-SP',
        name: 'Serbian (Latin) - Serbia'
    },
    {
        code: 'sk-SK',
        name: 'Slovak - Slovakia'
    },
    {
        code: 'sl-SI',
        name: 'Slovenian - Slovenia'
    },
    {
        code: 'es-AR',
        name: 'Spanish - Argentina'
    },
    {
        code: 'es-BO',
        name: 'Spanish - Bolivia'
    },
    {
        code: 'es-CL',
        name: 'Spanish - Chile'
    },
    {
        code: 'es-CO',
        name: 'Spanish - Colombia'
    },
    {
        code: 'es-CR',
        name: 'Spanish - Costa Rica'
    },
    {
        code: 'es-DO',
        name: 'Spanish - Dominican Republic'
    },
    {
        code: 'es-EC',
        name: 'Spanish - Ecuador'
    },
    {
        code: 'es-SV',
        name: 'Spanish - El Salvador'
    },
    {
        code: 'es-GT',
        name: 'Spanish - Guatemala'
    },
    {
        code: 'es-HN',
        name: 'Spanish - Honduras'
    },
    {
        code: 'es-MX',
        name: 'Spanish - Mexico'
    },
    {
        code: 'es-NI',
        name: 'Spanish - Nicaragua'
    },
    {
        code: 'es-PA',
        name: 'Spanish - Panama'
    },
    {
        code: 'es-PY',
        name: 'Spanish - Paraguay'
    },
    {
        code: 'es-PE',
        name: 'Spanish - Peru'
    },
    {
        code: 'es-PR',
        name: 'Spanish - Puerto Rico'
    },
    {
        code: 'es-ES',
        name: 'Spanish - Spain'
    },
    {
        code: 'es-UY',
        name: 'Spanish - Uruguay'
    },
    {
        code: 'es-VE',
        name: 'Spanish - Venezuela'
    },
    {
        code: 'sw-KE',
        name: 'Swahili - Kenya'
    },
    {
        code: 'sv-FI',
        name: 'Swedish - Finland'
    },
    {
        code: 'sv-SE',
        name: 'Swedish - Sweden'
    },
    {
        code: 'syr-SY',
        name: 'Syriac - Syria'
    },
    {
        code: 'ta-IN',
        name: 'Tamil - India'
    },
    {
        code: 'tt-RU',
        name: 'Tatar - Russia'
    },
    {
        code: 'te-IN',
        name: 'Telugu - India'
    },
    {
        code: 'th-TH',
        name: 'Thai - Thailand'
    },
    {
        code: 'tr-TR',
        name: 'Turkish - Turkey'
    },
    {
        code: 'uk-UA',
        name: 'Ukrainian - Ukraine'
    },
    {
        code: 'ur-PK',
        name: 'Urdu - Pakistan'
    },
    {
        code: 'Cy-uz-UZ',
        name: 'Uzbek (Cyrillic) - Uzbekistan'
    },
    {
        code: 'Lt-uz-UZ',
        name: 'Uzbek (Latin) - Uzbekistan'
    },
    {
        code: 'vi-VN',
        name: 'Vietnamese - Vietnam'
    }
];

let messagesAreLoaded = false;

export function loadMessages() {
    $.getJSON('./assets/messages.json')
        .then((data) => {
            Globalize.loadMessages(data);
            messagesAreLoaded = true;
        })
        .fail((e: any) => {
            if (e.status === 404) {
                console.log('Globalization: messages.json is not found. Localization is not provided.');
            } else {
                console.log('Globalization: messages.json - ' + e.statusText);
            }
        });
}

export type MessageName =
    | 'applyButtonTitle'
    | 'applyButtonTooltip'
    | 'clearSelections'
    | 'exportToExcel'
    | 'generatePDF'
    | 'gridSearchPanelPlaceHolder'
    | 'print'
    | 'refreshButtonCancelTitle'
    | 'refreshButtonCancelTooltip'
    | 'refreshButtonTitle'
    | 'refreshButtonTooltip'
    | 'resetFiltersButtonTitle'
    | 'resetFiltersButtonTooltip'
    | 'searchFieldPlaceHolder'
    | 'selectAll';

export type DefaultMessages<TMessageName extends MessageName> = Map<TMessageName, string>;

let _defaultMessages: DefaultMessages<MessageName>;

function getDefaultMessages<TMessageName extends MessageName>() {
    if (_defaultMessages == null) {
        _defaultMessages = new Map<MessageName, string>([
            ['applyButtonTitle', 'Apply'],
            ['applyButtonTooltip', 'Apply All Filter Changes'],
            ['clearSelections', 'Clear'],
            ['exportToExcel', 'Export to Excel'],
            ['generatePDF', 'Generate PDF'],
            ['gridSearchPanelPlaceHolder', 'Search'],
            ['print', 'Print'],
            ['refreshButtonCancelTitle', 'Cancel'],
            ['refreshButtonCancelTooltip', 'Cancel All Queries'],
            ['refreshButtonTitle', 'Refresh'],
            ['refreshButtonTooltip', 'Refresh Data'],
            ['resetFiltersButtonTitle', 'Reset Filters'],
            ['resetFiltersButtonTooltip', 'Reset Filters To Default'],
            ['searchFieldPlaceHolder', 'Search'],
            ['selectAll', 'Select All']
        ]);
    }

    return _defaultMessages as DefaultMessages<TMessageName>;
}

export function getMessage<TMessageName extends MessageName>(
    name: TMessageName,
    locale?: string,
    defaultMessages?: DefaultMessages<TMessageName>
) {
    if (defaultMessages === undefined) {
        defaultMessages = getDefaultMessages<TMessageName>();
    }

    if (!messagesAreLoaded) {
        return defaultMessages.get(name);
    }

    try {
        if (locale == null) {
            return Globalize.formatMessage(name);
        } else {
            return Globalize(locale).formatMessage(name);
        }
    } catch (e) {
        return defaultMessages.get(name);
    }
}
